import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@/components/ui/input-otp';

interface OtpCodeInputProps {
  /** Current 0-6 character value (digits only). */
  readonly value: string;
  /** Called on every change; parent controls the state. */
  readonly onChange: (next: string) => void;
  /** Auto-focus the first cell on mount. Default: true. */
  readonly autoFocus?: boolean;
  /** id of the helper text element (for aria-describedby). */
  readonly describedBy?: string;
  /** id of the first slot (use as label htmlFor target). */
  readonly id?: string;
}

/**
 * Six-cell verification-code input used across the auth flows
 * (confirm-signup, reset-password). Presentational only — parent owns
 * the state and decides what to do on completion (auto-submit, wait for
 * a button click, gate on another field, etc.).
 */
export function OtpCodeInput({
  value,
  onChange,
  autoFocus = true,
  describedBy,
  id,
}: OtpCodeInputProps) {
  return (
    <InputOTP
      // The only meaningful action on a code-entry screen is typing the
      // code; auto-focus is right here even though jsx-a11y disapproves
      // in general. Parents can opt out via `autoFocus={false}`.
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={autoFocus}
      maxLength={6}
      value={value}
      onChange={onChange}
      containerClassName="w-full"
      aria-describedby={describedBy}
      // Suppress password-manager autofill dropdowns — these are one-shot
      // email codes, not credentials worth storing. iOS native SMS code
      // autofill still works (different mechanism).
      data-1p-ignore
      data-lpignore="true"
      data-bwignore
      data-form-type="other"
    >
      <InputOTPGroup className="flex-1">
        <InputOTPSlot index={0} id={id} className="h-14 flex-1 text-xl" />
        <InputOTPSlot index={1} className="h-14 flex-1 text-xl" />
        <InputOTPSlot index={2} className="h-14 flex-1 text-xl" />
      </InputOTPGroup>
      <InputOTPSeparator />
      <InputOTPGroup className="flex-1">
        <InputOTPSlot index={3} className="h-14 flex-1 text-xl" />
        <InputOTPSlot index={4} className="h-14 flex-1 text-xl" />
        <InputOTPSlot index={5} className="h-14 flex-1 text-xl" />
      </InputOTPGroup>
    </InputOTP>
  );
}
